import { API_ENDPOINTS } from '@api/api.config';
import { apiClient } from '@api/api-clients';
import type { BookingResponse } from '@models/landing/api/api-response.model';
import type { Preferences } from '@models/master-data/master-data.model';
import { useAppStore } from '@store/app.store';
import { persistWebSessionId } from '@utils/web-session-query.constants';

export interface ValidatePnrParams {
  pnr: string;
  lastName: string;
}

interface ValidatePnrResponse {
  webSessionId?: string;
}

const readBodyWebSessionId = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const { webSessionId } = data as ValidatePnrResponse;
  return typeof webSessionId === 'string' && webSessionId.length > 0 ? webSessionId : null;
};

/** Uses validate body `webSessionId` only (same field as `redirectUrl`). */
const resolveWebSessionId = (data: unknown): string => {
  const webSessionId = readBodyWebSessionId(data);

  if (!webSessionId) {
    throw new Error('Validate PNR did not return a web session id');
  }

  return webSessionId.trim();
};

export const retrievePnr = (): Promise<BookingResponse> => {
  const selectedPaxForCheckIn = useAppStore.getState().setSelectedPaxForCheckIn;

  return apiClient.get<BookingResponse>(API_ENDPOINTS.PNR_RETRIEVE).then((response) => {
    const selectedPaxIds = response.data.selectedPax?.map((pax) => pax.resPaxId) ?? [];

    if (selectedPaxIds.length > 0) {
      selectedPaxForCheckIn(selectedPaxIds);
    }

    return response.data;
  });
};

/**
 * Validates PNR, persists the new web session, and returns it for navigation/retrieve.
 */
export const validatePnr = async ({ pnr, lastName }: ValidatePnrParams): Promise<string> => {
  const endpoint = API_ENDPOINTS.PNR_VALIDATE(pnr);
  const response = await apiClient.get<ValidatePnrResponse>(endpoint, {
    params: { lastName, pl: false },
  });
  const webSessionId = resolveWebSessionId(response.data);
  persistWebSessionId(webSessionId);
  return webSessionId;
};

export const getPreferences = async (): Promise<Preferences> => {
  const response = await apiClient.get<Preferences>(API_ENDPOINTS.PREFERENCES);
  return response.data;
};
